import React, { useState, useEffect, useCallback } from "react";

// Users tab component
export const UsersTab = ({ users, onEditUser, onDeleteUser, languages = [], onRowClick, onAdd }) => {
  const [filteredUsers, setFilteredUsers] = useState(users);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 20;

  React.useEffect(() => {
    let result = [...users];

    // Apply role filter
    if (roleFilter !== "all") {
      result = result.filter(user => user.role === roleFilter);
    }

    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(user =>
        user.nickname?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle special cases for sorting
      if (sortField === "created_at" || sortField === "last_signin") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredUsers(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [users, roleFilter, searchTerm, sortField, sortDirection]);

  // Get current users for pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handleSort = useCallback((field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  }, [sortField, sortDirection]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return "↕️";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  return (
    <div>
      <h2>Users</h2>
      <div style={{ marginBottom: "20px", display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button className="small-action-btn add" onClick={() => onAdd && onAdd()}>
          Add User
        </button>
        <input
          type="text"
          placeholder="Search by nickname or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginRight: "10px", padding: "5px" }}
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          style={{ padding: "5px" }}
        >
          <option value="all">All Roles</option>
          <option value="user">Users</option>
          <option value="admin">Admins</option>
        </select>
      </div>

      <div style={{ marginBottom: "10px" }}>
        Showing {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} users
      </div>

      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th onClick={() => handleSort("nickname")} style={{ cursor: "pointer" }}>
              Nickname <SortIcon field="nickname" />
            </th>
            <th onClick={() => handleSort("email")} style={{ cursor: "pointer" }}>
              Email <SortIcon field="email" />
            </th>
            <th onClick={() => handleSort("language")} style={{ cursor: "pointer" }}>
              Language <SortIcon field="language" />
            </th>
            <th onClick={() => handleSort("role")} style={{ cursor: "pointer" }}>
              Role <SortIcon field="role" />
            </th>
            <th onClick={() => handleSort("deck_count")} style={{ cursor: "pointer" }}>
              Decks <SortIcon field="deck_count" />
            </th>
            <th onClick={() => handleSort("last_signin")} style={{ cursor: "pointer" }}>
              Last Signin <SortIcon field="last_signin" />
            </th>
            <th onClick={() => handleSort("created_at")} style={{ cursor: "pointer" }}>
              Created <SortIcon field="created_at" />
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentUsers.map((user) => {
            const langObj = languages.find(l => l.code === user.language);
            return (
              <tr key={user.user_id} onClick={() => onRowClick && onRowClick('users', user)} style={{ cursor: 'pointer' }}>
                <td>{user.user_id}</td>
                <td>{user.nickname}</td>
                <td>{user.email}</td>
                <td>{langObj ? langObj.name : user.language}</td>
                <td>{user.role}</td>
                <td>{user.deck_count}</td>
                <td>{new Date(user.last_signin).toLocaleDateString()}</td>
                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                <td>
                  <button className="small-action-btn edit" onClick={(e) => { e.stopPropagation(); onEditUser(user); }}>Edit</button>
                  <button className="small-action-btn delete" onClick={(e) => {
                    e.stopPropagation();
                    if (user.deck_count > 0) {
                      alert(`Cannot delete: user owns ${user.deck_count} deck(s). Reassign or delete decks first.`);
                      return;
                    }
                    if (window.confirm(`Delete user "${user.nickname}"? This will also delete their canvases.`)) {
                      onDeleteUser(user.user_id);
                    }
                  }}>
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div style={{ marginTop: "20px" }}>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span style={{ margin: "0 10px" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// Add User Form
export function AddUserForm({ onAdd, onCancel, languages }) {
  const [nickname, setNickname] = useState("");
  const [email, setEmail] = useState("");
  const [language, setLanguage] = useState("en");
  const [role, setRole] = useState("user");
  const [canShare, setCanShare] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nickname.trim() || !email.trim()) {
      alert("Nickname and email are required");
      return;
    }

    try {
      await onAdd({
        nickname: nickname.trim(),
        email: email.trim(),
        language,
        role,
        can_share: canShare
      });
    } catch (err) {
      alert(err.message || "Failed to add user");
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <form onSubmit={handleSubmit} style={{ marginBottom: "20px", padding: "20px", border: "1px solid #ccc", borderRadius: "20px" }}>
        <h3>Add User</h3>

        <div style={{ marginBottom: "10px" }}>
          <label>Nickname:</label><br />
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            style={{
              width: "100%",
              maxWidth: "400px",
              marginLeft: "10px",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
              position: "static",
              left: "auto"
            }}
            required
            maxLength={50}
          />
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Email:</label><br />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              width: "100%",
              maxWidth: "400px",
              marginLeft: "10px",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
              position: "static",
              left: "auto"
            }}
            required
            maxLength={255}
          />
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Language:</label><br />
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            style={{
              width: "100%",
              maxWidth: "400px",
              marginLeft: "10px",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px"
            }}
          >
            {languages.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.name} ({lang.code})
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Role:</label><br />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            style={{
              width: "100%",
              maxWidth: "400px",
              marginLeft: "10px",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px"
            }}
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>
            <input
              type="checkbox"
              checked={canShare}
              onChange={(e) => setCanShare(e.target.checked)}
              style={{ marginRight: "5px" }}
            />
            Can Share
          </label>
        </div>

        <button type="submit">Add User</button>
        <button type="button" onClick={onCancel} style={{ marginLeft: "10px" }}>Cancel</button>
      </form>
    </div>
  );
}

// Edit User Form
export function EditUserForm({ user, onSave, onCancel, languages }) {
  const [nickname, setNickname] = useState(user.nickname || "");
  const [email, setEmail] = useState(user.email || "");
  const [language, setLanguage] = useState(user.language || "en");
  const [role, setRole] = useState(user.role || "user");
  const [canShare, setCanShare] = useState(user.can_share !== false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nickname.trim() || !email.trim()) {
      alert("Nickname and email are required");
      return;
    }

    try {
      await onSave({
        nickname: nickname.trim(),
        email: email.trim(),
        language,
        role,
        can_share: canShare
      });
    } catch (err) {
      alert(err.message || "Failed to update user");
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <form onSubmit={handleSubmit} style={{ marginBottom: "20px", padding: "20px", border: "1px solid #ccc", width: '100%', borderRadius: "20px" }}>
        <h3>Edit User</h3>

        <div style={{ marginBottom: "10px" }}>
          <label>Nickname:</label><br />
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            style={{
              width: "100%",
              maxWidth: "400px",
              marginLeft: "10px",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
              position: "static",
              left: "auto"
            }}
            required
            maxLength={50}
          />
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Email:</label><br />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              width: "100%",
              maxWidth: "400px",
              marginLeft: "10px",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
              position: "static",
              left: "auto"
            }}
            required
            maxLength={255}
          />
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Language:</label><br />
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            style={{
              width: "100%",
              maxWidth: "400px",
              marginLeft: "10px",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px"
            }}
          >
            {languages.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.name} ({lang.code})
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>Role:</label><br />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            style={{
              width: "100%",
              maxWidth: "400px",
              marginLeft: "10px",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px"
            }}
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div style={{ marginBottom: "10px" }}>
          <label>
            <input
              type="checkbox"
              checked={canShare}
              onChange={(e) => setCanShare(e.target.checked)}
              style={{ marginRight: "5px" }}
            />
            Can Share
          </label>
        </div>

        <button type="submit">Save User</button>
        <button type="button" onClick={onCancel} style={{ marginLeft: "10px" }}>Cancel</button>
      </form>
    </div>
  );
} 